import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Popconfirm, Tooltip, Spin } from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
    LoadingOutlined,
    UnorderedListOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { BASE_URL } from '@/config/serverApiConfig';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { usePermission } from '@/customHooks/usePermission';
import { formatName } from '@/utils/nameFormatter';
import WorkItemContextMenu from './modals/WorkItemContextMenu';

const PRIORITY_STYLES = {
    Critical: 'bg-[#ffdad6] text-[#93000a] dark:bg-red-950/80 dark:text-red-300 dark:border dark:border-red-800/50',
    High: 'bg-red-50 text-red-600 dark:bg-red-950/60 dark:text-red-400 dark:border dark:border-red-800/40',
    Medium: 'bg-[#c9e6ff] text-[#004c6e] dark:bg-blue-950/80 dark:text-blue-300 dark:border dark:border-blue-800/50',
    Low: 'bg-[#e7e8e9] text-[#424750] dark:bg-slate-800 dark:text-slate-300 dark:border dark:border-slate-700',
};

const STATUS_STYLES = {
    Draft: 'bg-purple-50 text-purple-700 dark:bg-purple-950/70 dark:text-purple-300 border border-dashed border-purple-300 dark:border-purple-700',
    Backlog: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:border dark:border-slate-700',
    'In Progress': 'bg-blue-50 text-blue-600 dark:bg-blue-950/70 dark:text-blue-300 dark:border dark:border-blue-800/50',
    'Under Review': 'bg-amber-50 text-amber-600 dark:bg-amber-950/70 dark:text-amber-300 dark:border dark:border-amber-800/50',
    Completed: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300 dark:border dark:border-emerald-800/50',
    Closed: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300 dark:border dark:border-emerald-800/50',
    Close: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300 dark:border dark:border-emerald-800/50',
    'On Hold': 'bg-orange-50 text-orange-600 dark:bg-orange-950/70 dark:text-orange-300 dark:border dark:border-orange-800/50',
    Cancelled: 'bg-red-50 text-red-400 dark:bg-red-950/50 dark:text-red-400 dark:border dark:border-red-800/40',
    Incomplete: 'bg-rose-50 text-rose-700 dark:bg-rose-950/80 dark:text-rose-300 dark:border dark:border-rose-800/60 font-bold',
};

const STATUS_DOT = {
    Draft: 'bg-purple-400',
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-300',
    Completed: 'bg-emerald-400',
    Closed: 'bg-emerald-400',
    Close: 'bg-emerald-400',
    'On Hold': 'bg-orange-300',
    Cancelled: 'bg-red-300',
    Incomplete: 'bg-rose-500',
};

// Progress bar color per status — matches approved design
const EFFORT_BAR_COLOR = {
    Draft: 'bg-purple-300',
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-300',
    Completed: 'bg-emerald-400',
    Closed: 'bg-emerald-400',
    Close: 'bg-emerald-400',
    'On Hold': 'bg-orange-300',
    Cancelled: 'bg-red-300',
    Incomplete: 'bg-rose-500',
};

const STATUS_OPTIONS = ['Backlog', 'In Progress', 'Under Review', 'Completed', 'On Hold', 'Cancelled', 'Incomplete'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseEstimatedToMs(str) {
    if (!str) return 0;
    let ms = 0;
    const h = str.match(/(\d+(?:\.\d+)?)h/);
    const m = str.match(/(\d+)m/);
    if (h) ms += parseFloat(h[1]) * 3600000;
    if (m) ms += parseInt(m[1]) * 60000;
    return ms;
}

function formatMs(ms) {
    if (!ms || ms <= 0) return '0h';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// EST/EFFORT cell — matches the approved design exactly
// Shows "12h / 8.5h" with a small progress bar below
function EffortCell({ estimatedTime, actualTimeMs, status }) {
    const estimatedMs = parseEstimatedToMs(estimatedTime);
    const actual = actualTimeMs || 0;
    const pct = estimatedMs > 0 ? Math.min(Math.round((actual / estimatedMs) * 100), 100) : 0;
    const overrun = estimatedMs > 0 && actual > estimatedMs;
    const barColor = overrun ? 'bg-red-400' : (EFFORT_BAR_COLOR[status] || 'bg-[#28b5fd]');

    return (
        <div className="text-center min-w-[96px]">
            <div className="text-sm font-bold text-[#191c1d] dark:text-white">
                {estimatedTime || '—'}
                {actual > 0 && (
                    <>
                        {' / '}
                        <span className={`${overrun ? 'text-red-500' : 'text-[#006591] dark:text-[#28b5fd]'}`}>
                            {formatMs(actual)}
                        </span>
                    </>
                )}
            </div>
            {estimatedMs > 0 && (
                <div className="w-20 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full mx-auto mt-1.5 overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all ${barColor}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}
        </div>
    );
}

function getInitials(name = '') {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

// Build full display name from name + surname fields (Admin model stores them separately)
// Returns the name in Title Case (first letter of each word capitalised).
function getFullName(person) {
    if (!person) return '';
    if (person.fullName) return formatName(person.fullName);
    const parts = [person.name, person.surname].filter(Boolean);
    const full = parts.join(' ').trim() || person.name || '';
    return formatName(full);
}

/**
 * Resolves an admin photo value to a fully-qualified URL.
 */
function AvatarCell({ assignee, isMilestone = false }) {
    const [imgError, setImgError] = useState(false);

    let photoUrl = null;
    if (assignee && typeof assignee === 'object') {
        if (assignee.photo) {
            photoUrl = assignee.photo.startsWith('http')
                ? assignee.photo
                : `${BASE_URL.replace(/\/api\/?$/, '')}/${assignee.photo.replace(/^\//, '')}`;
        }
    }

    React.useEffect(() => {
        setImgError(false);
    }, [photoUrl]);

    if (!assignee) {
        return (
            <div className="flex items-center gap-2">
                <div
                    className="rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-bold text-slate-400 flex-shrink-0 overflow-hidden"
                    style={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                >?</div>
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-[#424750] dark:text-slate-400">Unassigned</span>
                    {isMilestone && <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">Owner</span>}
                </div>
            </div>
        );
    }

    const fullName = getFullName(assignee);
    const showPhoto = photoUrl && !imgError;

    return (
        <div className="flex items-center gap-2">
            <div
                style={{
                    width: 28,
                    height: 28,
                    minWidth: 28,
                    minHeight: 28,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: showPhoto ? 'transparent' : 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)',
                }}
            >
                {showPhoto
                    ? <img
                        src={photoUrl}
                        alt={fullName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '50%' }}
                        onError={() => setImgError(true)}
                    />
                    : getInitials(fullName)
                }
            </div>
            <div className="flex flex-col leading-tight">
                <span className="text-sm font-bold text-[#191c1d] dark:text-white truncate max-w-[130px]">
                    {fullName}
                </span>
                {isMilestone && (
                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                        Owner
                    </span>
                )}
            </div>
        </div>
    );
}

// Column definitions — order matches approved design: Title · Assignee · Project · Priority · Stage · EST/EFFORT · Due Date · Actions
const COLUMNS = [
    { key: 'title', label: 'Title / Task ID', align: 'left', rounded: 'tl', minWidth: '240px' },
    { key: 'assignee', label: 'Assignee', align: 'left', minWidth: '150px' },
    { key: 'project', label: 'Project', align: 'left', minWidth: '140px' },
    { key: 'milestone', label: 'Milestone', align: 'left', minWidth: '130px' },
    { key: 'priority', label: 'Priority', align: 'left', minWidth: '100px' },
    { key: 'stage', label: 'Stage', align: 'left', minWidth: '130px' },
    { key: 'effort', label: 'Est/Effort', align: 'center', minWidth: '110px' },
    { key: 'due', label: 'Due Date', align: 'left', minWidth: '130px' },
    { key: 'actions', label: 'Actions', align: 'center', rounded: 'tr', minWidth: '90px' },
];

export default function TaskListTable({
    tasks = [],
    loading = false,
    pagination = {},
    onEdit,
    onDelete,
    onStatusChange,
    onPageChange,
    onRowClick,
    onProjectClick,
    onConvertType,
    onAddSubtask,
    updatingStatusId = null, // taskId currently having status updated
    deletingId = null,        // taskId currently being deleted
    searchQuery = '',
    onClearSearch = null,
    hasActiveFilters = false,
    onResetFilters = null,
    projects = [],
}) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const userRole = (currentAdmin?.role || '').toLowerCase();
    const isEmployee = userRole === 'employee';
    const { canDelete: canDeletePM } = usePermission('ProjectManagement');
    const isElevated = ['super_admin', 'admin', 'project manager', 'manager'].includes(userRole);

    const { total = 0, page = 1, totalPages = 1 } = pagination;

    const displayTasks = React.useMemo(() => (tasks || []).filter(t => t?.itemType !== 'milestone'), [tasks]);

    if (loading && displayTasks.length === 0) {
        return (
            <div className="bg-white dark:bg-[#1f1f1f] border border-slate-100 dark:border-[#303030] rounded-2xl shadow-[0px_8px_24px_rgba(25,28,29,0.06)] overflow-hidden">
                <div className="flex items-center justify-center py-16 gap-3 text-[#424750] dark:text-slate-400">
                    <LoadingOutlined style={{ fontSize: 22 }} />
                    <span className="text-sm font-medium">Loading tasks…</span>
                </div>
            </div>
        );
    }

    return (
        <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />}>
            <div className="bg-white dark:bg-[#1f1f1f] border border-slate-100 dark:border-[#303030] rounded-2xl shadow-[0px_8px_24px_rgba(25,28,29,0.06)] overflow-hidden transition-opacity">
            <style>{`
                .task-table-scroll::-webkit-scrollbar {
                    height: 8px;
                }
                .task-table-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .task-table-scroll::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 4px;
                }
                .task-table-scroll::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
                .dark .task-table-scroll::-webkit-scrollbar-thumb {
                    background: #475569;
                }
                .dark .task-table-scroll::-webkit-scrollbar-thumb:hover {
                    background: #64748b;
                }
            `}</style>
            <div className="overflow-x-auto task-table-scroll">
                <table className="w-full min-w-[1100px] text-left border-collapse">
                    <thead>
                        <tr className="bg-[#f8fafc] dark:bg-[#262626] border-b border-slate-100 dark:border-[#303030]">
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    data-tour={`col-${col.key}`}
                                    style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                                    className={`px-6 py-4 font-headline text-xs font-bold uppercase tracking-wider text-[#64748b] dark:text-[#94a3b8] whitespace-nowrap
                                        ${col.align === 'center' ? 'text-center' : ''}
                                        ${col.rounded === 'tl' ? 'rounded-tl-xl' : ''}
                                        ${col.rounded === 'tr' ? 'rounded-tr-xl' : ''}
                                    `}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {displayTasks.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length} className="px-6 py-16 text-center">
                                    {(searchQuery || hasActiveFilters) ? (
                                        <div className="flex flex-col items-center gap-3 text-[#424750] dark:text-slate-400 max-w-sm mx-auto">
                                            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                                <SearchOutlined style={{ fontSize: 22 }} className="text-slate-400" />
                                            </div>
                                            <p className="text-base font-bold text-slate-800 dark:text-slate-200">
                                                {searchQuery ? `No tasks found matching "${searchQuery}"` : 'No tasks match active filters'}
                                            </p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                Check your spelling or reset your filters to view all tasks.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onClearSearch?.();
                                                    onResetFilters?.();
                                                }}
                                                className="mt-1 px-4 py-1.5 rounded-xl text-xs font-bold text-[#03488a] dark:text-[#a7c8ff] bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors border border-blue-200 dark:border-blue-800 cursor-pointer"
                                            >
                                                Clear Search & Filters
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-3 text-[#424750] dark:text-slate-400">
                                            <UnorderedListOutlined style={{ fontSize: 36 }} />
                                            <p className="text-sm font-medium">No work items found.</p>
                                            <p className="text-xs">Click "+ New Task" to create your first item.</p>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ) : (
                            displayTasks.map((task, idx) => {
                                const isMilestone = task.itemType === 'milestone';
                                // A task is a Subtask only when it has a real parent task (parentId)
                                // or its itemType is 'subtask'. Tasks directly under a milestone are parent tasks.
                                const isSubtask = Boolean(task.parentId || task.itemType === 'subtask');

                                return (
                                    <tr
                                        key={task._id}
                                        data-tour={idx === 0 ? "table-task-row" : undefined}
                                        className={`transition-all hover:-translate-y-px border-b border-slate-100 dark:border-[#303030]
                                            ${idx % 2 !== 0 ? 'bg-[#f3f4f5]/30 dark:bg-[#141414]' : 'bg-white dark:bg-[#1f1f1f]'}
                                            hover:bg-[#f3f4f5] dark:hover:bg-[#262626]
                                        `}
                                    >
                                        {/* Task / Milestone Title — wrapped in Context Menu */}
                                        <td className="px-6 py-4 cursor-pointer max-w-[400px]" onClick={() => onRowClick?.(task)}>
                                            <WorkItemContextMenu
                                                item={task}
                                                onView={onRowClick}
                                                onEdit={onEdit}
                                                onStatusChange={onStatusChange}
                                                onAddSubtask={onAddSubtask}
                                                onConvertType={onConvertType}
                                                onDelete={onDelete}
                                            >
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    {/* Work Item Type Icon: ◆ Diamond for Milestone vs ○ Circle for Task */}
                                                    {isMilestone ? (
                                                        <span className="text-amber-500 font-bold text-lg shrink-0 leading-none" title="Milestone">
                                                            ◆
                                                        </span>
                                                    ) : (
                                                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[task.status] || 'bg-slate-300'}`} title="Task" />
                                                    )}

                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <Tooltip title={task.title} placement="topLeft" mouseEnterDelay={0.2}>
                                                                <span className="font-bold text-[#191c1d] dark:text-white text-base hover:text-[#03488a] dark:hover:text-[#a7c8ff] transition-colors truncate max-w-[260px] sm:max-w-[320px] inline-block align-middle cursor-pointer">
                                                                    {task.title}
                                                                </span>
                                                            </Tooltip>
                                                            {isMilestone && (
                                                                <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                                                                    Milestone
                                                                </span>
                                                            )}
                                                            {!isMilestone && isSubtask && (
                                                                <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-950/80 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                                                    Subtask
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <p className="text-xs text-[#737782] dark:text-slate-400 font-mono truncate">{task.taskId}</p>
                                                            {task.generationSource && task.generationSource !== 'manual' && (() => {
                                                                const srcConfig = {
                                                                    ai: { label: '⚡ AI', className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-800' },
                                                                    cre: { label: '🏗 CRE', className: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/80 dark:text-blue-300 dark:border-blue-800' },
                                                                    asana: { label: '🔄 Asana', className: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/80 dark:text-purple-300 dark:border-purple-800' },
                                                                    system: { label: '⚙️ System', className: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-800' },
                                                                };
                                                                const c = srcConfig[task.generationSource];
                                                                return c ? (
                                                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border whitespace-nowrap ${c.className}`}>
                                                                        {c.label}
                                                                    </span>
                                                                ) : null;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </WorkItemContextMenu>
                                        </td>

                                        {/* Assignee / Milestone Owner */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <AvatarCell
                                                assignee={isMilestone ? (task.milestoneOwner || task.assignee) : task.assignee}
                                                isMilestone={isMilestone}
                                            />
                                        </td>

                                        {/* Project — Primary + Additional */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(() => {
                                                const primaryProject = (typeof task.project === 'object' && task.project !== null ? task.project.name : null) || task.projectName || (typeof task.project === 'string' ? task.project : null) || '—';
                                                const primaryProjectId = (typeof task.project === 'object' && task.project !== null ? (task.project._id || task.project.id) : null) || task.projectId || (typeof task.project === 'string' && /^[0-9a-fA-F]{24}$/.test(task.project) ? task.project : null);
                                                const additionalProjects = (task.additionalProjects || []).map(p => (typeof p === 'object' && p !== null ? p?.name : p) || p);
                                                const allProjects = [primaryProject, ...additionalProjects].filter(p => p !== '—');

                                                const handleProjectBadgeClick = (e) => {
                                                    e.stopPropagation();
                                                    if (primaryProjectId && onProjectClick) {
                                                        onProjectClick(task.project || primaryProjectId);
                                                    }
                                                };

                                                // If only 1 project
                                                if (allProjects.length <= 1) {
                                                    return (
                                                        <Tooltip title={primaryProjectId ? `Open project: ${primaryProject}` : primaryProject} placement="top">
                                                            <span
                                                                onClick={handleProjectBadgeClick}
                                                                className={`text-xs font-bold text-[#424750] dark:text-slate-300 bg-[#e7e8e9] dark:bg-[#334155] px-2.5 py-1 rounded-md truncate max-w-[150px] inline-block transition-colors ${primaryProjectId && onProjectClick ? 'cursor-pointer hover:bg-[#03488a] hover:text-white dark:hover:bg-blue-700 dark:hover:text-white' : ''}`}
                                                            >
                                                                {primaryProject}
                                                            </span>
                                                        </Tooltip>
                                                    );
                                                }

                                                // Multiple projects — show primary + count with tooltip
                                                return (
                                                    <Tooltip
                                                        title={
                                                            <div className="text-xs space-y-1">
                                                                {allProjects.map((proj, idx) => (
                                                                    <div key={idx}>• {proj}</div>
                                                                ))}
                                                            </div>
                                                        }
                                                        placement="top"
                                                    >
                                                        <span
                                                            onClick={handleProjectBadgeClick}
                                                            className={`text-xs font-bold text-[#424750] dark:text-slate-300 bg-[#e7e8e9] dark:bg-[#334155] px-2.5 py-1 rounded-md truncate max-w-[150px] inline-block transition-colors ${primaryProjectId && onProjectClick ? 'cursor-pointer hover:bg-[#03488a] hover:text-white dark:hover:bg-blue-700 dark:hover:text-white' : 'cursor-help'}`}
                                                        >
                                                            {primaryProject} +{additionalProjects.length}
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </td>

                                        {/* Milestone — shows linked milestone name or '—' */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(() => {
                                                if (isMilestone) return <span className="text-xs text-slate-400">—</span>;
                                                const ms = task.milestoneId || (task.parentId?.itemType === 'milestone' ? task.parentId : null);
                                                if (!ms) return <span className="text-xs text-slate-400">—</span>;

                                                let msTitle = '';
                                                if (typeof ms === 'object') {
                                                    msTitle = ms.title || ms.name || '';
                                                } else if (typeof ms === 'string') {
                                                    const foundInTasks = (tasks || []).find(t => String(t._id) === String(ms));
                                                    if (foundInTasks) {
                                                        msTitle = foundInTasks.title || foundInTasks.name || '';
                                                    } else if (task.project?.milestones) {
                                                        const foundInProj = task.project.milestones.find(m => String(m._id || m.id) === String(ms));
                                                        if (foundInProj) msTitle = foundInProj.title || foundInProj.name || '';
                                                    }
                                                    if (!msTitle && Array.isArray(projects)) {
                                                        for (const proj of projects) {
                                                            if (Array.isArray(proj.milestones)) {
                                                                const found = proj.milestones.find(m => String(m._id || m.id) === String(ms));
                                                                if (found) {
                                                                    msTitle = found.title || found.name || '';
                                                                    break;
                                                                }
                                                            }
                                                        }
                                                    }
                                                }

                                                if (!msTitle || msTitle === '—') {
                                                    return <span className="text-xs text-slate-400">—</span>;
                                                }

                                                return (
                                                    <Tooltip title={msTitle} placement="top">
                                                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded-md border border-amber-200 dark:border-amber-800/50 max-w-[140px] truncate">
                                                            <span className="text-amber-500 shrink-0">◆</span>
                                                            <span className="truncate">{msTitle}</span>
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </td>

                                        {/* Priority */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium}`}>
                                                {task.priority}
                                            </span>
                                        </td>

                                        {/* Stage — inline select */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {(() => {
                                                const resolvedStatus = ['Closed', 'Close'].includes(task.status) ? 'Completed' : (task.status || 'Backlog');
                                                return updatingStatusId === task._id ? (
                                                    <div className="flex items-center gap-2">
                                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} />} />
                                                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLES[resolvedStatus] || ''}`}>
                                                            {resolvedStatus}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <select
                                                        value={resolvedStatus}
                                                        onChange={(e) => { e.stopPropagation(); onStatusChange(task._id, e.target.value); }}
                                                        className={`text-xs font-bold px-3 py-1 rounded-full border-none cursor-pointer focus:ring-2 focus:ring-[#28b5fd] outline-none ${STATUS_STYLES[resolvedStatus] || ''}`}
                                                    >
                                                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                );
                                            })()}
                                        </td>

                                        {/* EST/EFFORT or Milestone Progress */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {isMilestone ? (
                                                <div className="text-center min-w-[100px]">
                                                    <div className="text-xs font-bold text-[#03488a] dark:text-[#a7c8ff]">
                                                        {task.completedChildTasks ?? 0} / {task.totalChildTasks ?? 0} Tasks ({task.progress ?? 0}%)
                                                    </div>
                                                    <div className="w-20 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full mx-auto mt-1.5 overflow-hidden">
                                                        <div
                                                             className="h-full rounded-full transition-all bg-emerald-500"
                                                            style={{ width: `${task.progress ?? 0}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <EffortCell
                                                    estimatedTime={task.estimatedTime}
                                                    actualTimeMs={task.actualTimeMs}
                                                    status={task.status}
                                                />
                                            )}
                                        </td>

                                        {/* Due Date */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {task.dueDate ? (
                                                <span className={`text-sm font-bold whitespace-nowrap ${new Date(task.dueDate) < new Date() && !['Completed', 'Cancelled', 'Closed', 'Close'].includes(task.status) ? 'text-red-500' : 'text-[#191c1d] dark:text-white'}`}>
                                                    {dayjs(task.dueDate).format('MMM D, YYYY')}
                                                </span>
                                            ) : (
                                                <span className="text-sm text-[#737782] dark:text-slate-500 whitespace-nowrap">—</span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center justify-center gap-2">
                                                <Tooltip title={isMilestone ? "Edit milestone" : "Edit task"}>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                                                        disabled={deletingId === task._id || updatingStatusId === task._id}
                                                        className="p-1.5 rounded-lg hover:bg-[#e7e8e9] dark:hover:bg-[#334155] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        <EditOutlined className="text-[#424750] dark:text-slate-300" style={{ fontSize: 16 }} />
                                                    </button>
                                                </Tooltip>

                                                {(() => {
                                                    const isCreator = task.createdBy && String(task.createdBy._id || task.createdBy) === String(currentAdmin?._id);
                                                    const canDeleteThisItem = isElevated || canDeletePM || isCreator;
                                                    if (!canDeleteThisItem) return null;
                                                    return (
                                                        <Popconfirm
                                                            title={isMilestone ? "Delete this milestone?" : "Delete this task?"}
                                                            description="This cannot be undone."
                                                            onConfirm={() => onDelete(task._id)}
                                                            okText="Delete"
                                                            okButtonProps={{
                                                                danger: true,
                                                                loading: deletingId === task._id,
                                                            }}
                                                            cancelText="Cancel"
                                                            placement="topRight"
                                                        >
                                                            <Tooltip title="Delete">
                                                                <button
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    disabled={updatingStatusId === task._id}
                                                                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                                                >
                                                                    {deletingId === task._id
                                                                        ? <LoadingOutlined className="text-red-500" style={{ fontSize: 16 }} />
                                                                        : <DeleteOutlined className="text-[#737782] dark:text-slate-400" style={{ fontSize: 16 }} />
                                                                    }
                                                                </button>
                                                            </Tooltip>
                                                        </Popconfirm>
                                                    );
                                                })()}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {tasks.length > 0 && (
                <div data-tour="table-pagination" className="px-6 py-4 bg-[#f3f4f5]/50 dark:bg-[#263347]/50 flex justify-between items-center border-t border-slate-100 dark:border-slate-700">
                    <p className="text-xs font-bold text-[#424750] dark:text-slate-400">
                        Showing {Math.min((page - 1) * (pagination.limit || 20) + 1, total)}–{Math.min((page - 1) * (pagination.limit || 20) + tasks.length, total)} of {total} task{total !== 1 ? 's' : ''}
                    </p>

                    {totalPages > 1 && (
                        <div className="flex items-center gap-1">
                            {/* Prev */}
                            <button
                                onClick={() => onPageChange(page - 1)}
                                disabled={page <= 1}
                                className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center hover:bg-white dark:hover:bg-[#334155] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                <LeftOutlined style={{ fontSize: 11 }} />
                            </button>

                            {/* Page number buttons — show up to 5, with ellipsis */}
                            {(() => {
                                const pages = [];
                                const delta = 2;
                                const left = Math.max(1, page - delta);
                                const right = Math.min(totalPages, page + delta);

                                if (left > 1) {
                                    pages.push(1);
                                    if (left > 2) pages.push('...');
                                }
                                for (let p = left; p <= right; p++) pages.push(p);
                                if (right < totalPages) {
                                    if (right < totalPages - 1) pages.push('...');
                                    pages.push(totalPages);
                                }

                                return pages.map((p, i) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-xs text-[#737782] dark:text-slate-500">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => onPageChange(p)}
                                            className="w-8 h-8 rounded-lg text-xs font-bold transition-all"
                                            style={page === p
                                                ? { background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)', color: '#fff' }
                                                : {}}
                                            {...(page !== p ? { className: 'w-8 h-8 rounded-lg text-xs font-bold transition-all text-[#424750] dark:text-slate-400 hover:bg-white dark:hover:bg-[#334155] border border-slate-200 dark:border-slate-600' } : {})}
                                        >
                                            {p}
                                        </button>
                                    )
                                );
                            })()}

                            {/* Next */}
                            <button
                                onClick={() => onPageChange(page + 1)}
                                disabled={page >= totalPages}
                                className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center hover:bg-white dark:hover:bg-[#334155] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                <RightOutlined style={{ fontSize: 11 }} />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
        </Spin>
    );
}




